import type {
  KartHorn,
  KartPattern,
  KartState,
  KartSticker,
  KartTopper,
  KartTrail,
  KartWeightClass,
} from "./simulation/types";
import { LEGACY_DRIVER_PROFILES } from "./legacyDriverProfiles";

export { LEGACY_DRIVER_PROFILES } from "./legacyDriverProfiles";
export { selectLegacyDriverProfile } from "./legacyDriverProfiles";

export interface DriverProfileTraits {
  /** 0..1: willingness to attempt a pass and accept a narrower opening. */
  passAggression: number;
  /** Track-space amplitude in metres, bounded to 1.4..4.8. */
  laneWidth: number;
  /** Ambient line-change frequency in radians/second, bounded to 0.1..0.34. */
  laneTempo: number;
  /** 0..1: deterministic willingness to commit to a course shortcut. */
  shortcutTendency: number;
  /** 0..1: lowers the corner threshold for beginning a drift. */
  driftCommitment: number;
  /** 0..1: how long an item is held for a better tactical opening. */
  itemPatience: number;
  /** Small decision-only throttle bias; never changes kart physics or class stats. */
  paceBias: number;
  /** 0..1: willingness to cover a closing rival instead of holding the ambient line. */
  defenseTendency: number;
  /** 0..1: how early the driver reads and avoids an available track hazard. */
  hazardAwareness: number;
}

export interface DriverItemTactics {
  /** Maximum useful gap for launching a Rolling Keg. */
  kegRange: number;
  /** Rear threat range that triggers a Foam Slick. */
  foamThreatRange: number;
  /** Position at or behind which a shield is equipped without a nearby threat. */
  shieldPlace: number;
  /** Position at or behind which Last Orders is fired immediately. */
  comebackPlace: number;
  /** Maximum sampled corner severity accepted for a boost item. */
  boostCurvature: number;
}

export interface DriverProfileAppearance {
  color: number;
  accentColor: number;
  variant: number;
  livery: number;
  wheelStyle: number;
  driverColor: number;
  weightClass: KartWeightClass;
  pattern: KartPattern;
  topper: KartTopper;
  sticker: KartSticker;
  underglow: number;
  horn: KartHorn;
  skidColor: number;
  trail: KartTrail;
  raceNumber: string;
}

export interface DriverProfileBarks {
  intro: readonly string[];
  pass: readonly string[];
  hit: readonly string[];
  win: readonly string[];
}

export type DriverCelebrationStyle = "fist-pump" | "wild-wave" | "stoic-nod" | "warrior-punch" | "scout-salute" | "precision-clap" | "storm-thrash";

export interface DriverAnimationProfile {
  celebration: DriverCelebrationStyle;
  /** Multiplier for steering and drift body language. */
  lean: number;
  /** Multiplier for the small road-speed bounce. */
  bob: number;
  /** Personal movement tempo, in cycles per second. */
  tempo: number;
  /** Ambient head movement while the kart is settled. */
  headScan: number;
}

export interface DriverProfile {
  id: string;
  kartId: string;
  name: string;
  title: string;
  signature: string;
  color: number;
  appearance: Readonly<DriverProfileAppearance>;
  traits: Readonly<DriverProfileTraits>;
  itemTactics: Readonly<DriverItemTactics>;
  barks: Readonly<DriverProfileBarks>;
  animation: Readonly<DriverAnimationProfile>;
}

export const RIVAL_PROFILES: readonly DriverProfile[] = [
  {
    id: "malt-maverick", kartId: "kart-1", name: "MALT MAVERICK", title: "The Copper Comet", signature: "Late-braking outside passes",
    color: 0xef654d,
    appearance: { color: 0xef654d, accentColor: 0xffd45c, variant: 0, livery: 2, wheelStyle: 0, driverColor: 0xffc08a, weightClass: "medium", pattern: "stripe", topper: "cap", sticker: "hop", underglow: 0xff6b36, horn: "lager", skidColor: 0xff9b52, trail: "flame", raceNumber: "01" },
    traits: { passAggression: 0.94, laneWidth: 4.2, laneTempo: 0.25, shortcutTendency: 0.72, driftCommitment: 0.82, itemPatience: 0.28, paceBias: 0.035, defenseTendency: 0.45, hazardAwareness: 0.62 },
    itemTactics: { kegRange: 92, foamThreatRange: 28, shieldPlace: 4, comebackPlace: 6, boostCurvature: 0.55 },
    barks: { intro: ["Make way for the Maverick!"], pass: ["Copper flash!"], hit: ["Watch the paint!"], win: ["First round is mine!"] },
    animation: { celebration: "fist-pump", lean: 1.12, bob: 1.05, tempo: 1.15, headScan: 0.45 },
  },
  {
    id: "hops-hazard", kartId: "kart-2", name: "HOPS HAZARD", title: "The Green Menace", signature: "Restless lines and shortcut dives",
    color: 0x36a870,
    appearance: { color: 0x36a870, accentColor: 0xb9f25f, variant: 1, livery: 2, wheelStyle: 1, driverColor: 0x83e4aa, weightClass: "light", pattern: "rally", topper: "lime", sticker: "bolt", underglow: 0x49ff9a, horn: "air", skidColor: 0x7ce58c, trail: "hops", raceNumber: "77" },
    traits: { passAggression: 0.78, laneWidth: 4.8, laneTempo: 0.34, shortcutTendency: 0.96, driftCommitment: 0.69, itemPatience: 0.18, paceBias: 0.018, defenseTendency: 0.25, hazardAwareness: 0.38 },
    itemTactics: { kegRange: 74, foamThreatRange: 24, shieldPlace: 5, comebackPlace: 6, boostCurvature: 0.68 },
    barks: { intro: ["Hop to it!"], pass: ["Mind the Hazard!"], hit: ["Bitter!"], win: ["Freshest racer in the field!"] },
    animation: { celebration: "wild-wave", lean: 1.28, bob: 1.32, tempo: 1.5, headScan: 0.9 },
  },
  {
    id: "porter-pat", kartId: "kart-3", name: "PORTER PAT", title: "The Dark Horse", signature: "Patient defence and heavy counters",
    color: 0x3a243f,
    appearance: { color: 0x3a243f, accentColor: 0xc89ee8, variant: 6, livery: 3, wheelStyle: 4, driverColor: 0x7b526f, weightClass: "heavy", pattern: "split", topper: "crown", sticker: "pint", underglow: 0x8b5bd3, horn: "bell", skidColor: 0x724d86, trail: "foam", raceNumber: "88" },
    traits: { passAggression: 0.34, laneWidth: 1.8, laneTempo: 0.12, shortcutTendency: 0.22, driftCommitment: 0.31, itemPatience: 0.92, paceBias: -0.012, defenseTendency: 1, hazardAwareness: 0.95 },
    itemTactics: { kegRange: 55, foamThreatRange: 44, shieldPlace: 2, comebackPlace: 7, boostCurvature: 0.32 },
    barks: { intro: ["Slow pour. Strong finish."], pass: ["Patience pays."], hit: ["Not a drop spilled."], win: ["Worth the wait."] },
    animation: { celebration: "stoic-nod", lean: 0.68, bob: 0.55, tempo: 0.72, headScan: 0.18 },
  },
  {
    id: "lagertha", kartId: "kart-4", name: "LAGERTHA", title: "Shieldmaiden of Speed", signature: "Committed drifts and fearless attacks",
    color: 0xd5b64c,
    appearance: { color: 0xd5b64c, accentColor: 0xf8ecb1, variant: 4, livery: 4, wheelStyle: 2, driverColor: 0xf1d183, weightClass: "light", pattern: "stripe", topper: "crown", sticker: "hop", underglow: 0xffdf59, horn: "air", skidColor: 0xf4c542, trail: "foam", raceNumber: "04" },
    traits: { passAggression: 1, laneWidth: 3.6, laneTempo: 0.29, shortcutTendency: 0.64, driftCommitment: 1, itemPatience: 0.12, paceBias: 0.04, defenseTendency: 0.2, hazardAwareness: 0.48 },
    itemTactics: { kegRange: 105, foamThreatRange: 32, shieldPlace: 4, comebackPlace: 6, boostCurvature: 0.72 },
    barks: { intro: ["To the front!"], pass: ["Your line is mine!"], hit: ["A glancing blow."], win: ["Victory is served!"] },
    animation: { celebration: "warrior-punch", lean: 1.34, bob: 1.15, tempo: 1.28, headScan: 0.36 },
  },
  {
    id: "stout-scout", kartId: "kart-5", name: "STOUT SCOUT", title: "The Trailfinder", signature: "Measured scouting lines and safe shortcuts",
    color: 0x70422b,
    appearance: { color: 0x70422b, accentColor: 0xe49b58, variant: 3, livery: 2, wheelStyle: 3, driverColor: 0xb87545, weightClass: "heavy", pattern: "rally", topper: "cap", sticker: "bolt", underglow: 0xd36a32, horn: "toot", skidColor: 0x8c5437, trail: "bubbles", raceNumber: "15" },
    traits: { passAggression: 0.48, laneWidth: 2.4, laneTempo: 0.17, shortcutTendency: 0.58, driftCommitment: 0.44, itemPatience: 0.72, paceBias: -0.004, defenseTendency: 0.72, hazardAwareness: 0.9 },
    itemTactics: { kegRange: 62, foamThreatRange: 38, shieldPlace: 3, comebackPlace: 7, boostCurvature: 0.4 },
    barks: { intro: ["Route checked. Ready."], pass: ["Found a way through."], hit: ["Recalculating."], win: ["The trail led here."] },
    animation: { celebration: "scout-salute", lean: 0.82, bob: 0.72, tempo: 0.88, headScan: 0.62 },
  },
  {
    id: "pilsner-pete", kartId: "kart-6", name: "PILSNER PETE", title: "The Clean-Line King", signature: "Tidy apexes and perfectly timed items",
    color: 0xe9d86f,
    appearance: { color: 0xe9d86f, accentColor: 0xf8fff0, variant: 2, livery: 0, wheelStyle: 1, driverColor: 0xf7e79a, weightClass: "medium", pattern: "solid", topper: "lime", sticker: "pint", underglow: 0xd7ff80, horn: "lager", skidColor: 0xd5c875, trail: "neon", raceNumber: "06" },
    traits: { passAggression: 0.58, laneWidth: 1.4, laneTempo: 0.1, shortcutTendency: 0.36, driftCommitment: 0.62, itemPatience: 0.64, paceBias: 0.012, defenseTendency: 0.6, hazardAwareness: 1 },
    itemTactics: { kegRange: 78, foamThreatRange: 26, shieldPlace: 4, comebackPlace: 7, boostCurvature: 0.46 },
    barks: { intro: ["Keep it crisp."], pass: ["Clean and clear."], hit: ["That muddied the finish."], win: ["A perfect pour!"] },
    animation: { celebration: "precision-clap", lean: 0.9, bob: 0.8, tempo: 1, headScan: 0.28 },
  },
  {
    id: "ale-storm", kartId: "kart-7", name: "ALE STORM", title: "The Thunder Tap", signature: "Wild pace and instant item pressure",
    color: 0x3151a5,
    appearance: { color: 0x3151a5, accentColor: 0xff4fa3, variant: 5, livery: 5, wheelStyle: 4, driverColor: 0x8ca7ff, weightClass: "medium", pattern: "split", topper: "crown", sticker: "bolt", underglow: 0x4edcff, horn: "bell", skidColor: 0x56b9ff, trail: "neon", raceNumber: "99" },
    traits: { passAggression: 0.9, laneWidth: 4.5, laneTempo: 0.31, shortcutTendency: 0.84, driftCommitment: 0.88, itemPatience: 0, paceBias: 0.03, defenseTendency: 0.35, hazardAwareness: 0.3 },
    itemTactics: { kegRange: 120, foamThreatRange: 40, shieldPlace: 3, comebackPlace: 5, boostCurvature: 0.75 },
    barks: { intro: ["Storm warning!"], pass: ["Thunder on your shoulder!"], hit: ["Just turbulence."], win: ["The Storm takes it!"] },
    animation: { celebration: "storm-thrash", lean: 1.22, bob: 1.22, tempo: 1.65, headScan: 0.74 },
  },
] as const;

const ALL_DRIVER_PROFILES = [...RIVAL_PROFILES, ...LEGACY_DRIVER_PROFILES] as const;
const PROFILE_BY_ID = new Map(ALL_DRIVER_PROFILES.map((profile) => [profile.id, profile]));
const PROFILE_BY_KART_ID = new Map(ALL_DRIVER_PROFILES.map((profile) => [profile.kartId, profile]));

export function getDriverProfileForKart(kartOrId: Pick<KartState, "id" | "driverProfileId"> | string): DriverProfile | null {
  if (typeof kartOrId !== "string") {
    const explicit = kartOrId.driverProfileId ? PROFILE_BY_ID.get(kartOrId.driverProfileId) : undefined;
    if (explicit) return explicit;
  }
  const kartId = typeof kartOrId === "string" ? kartOrId : kartOrId.id;
  return PROFILE_BY_KART_ID.get(kartId) ?? null;
}

export function applyDriverProfileAppearance(kart: KartState, profile: DriverProfile): void {
  kart.name = profile.name;
  Object.assign(kart, profile.appearance);
}
